'use client'
import { getLeadNotifications } from '@/actions/data-fetch-settings';
import Button from '@/components/common/Button';
import ChannelIcon from '@/components/common/ChannelIcon';
import Toggle from '@/components/common/Toggle';
// Define NotificationSetting type locally since @/types/notifications doesn't exist
interface NotificationSetting {
  uuid: string;
  name: string;
  description: string;
  enabled: boolean;
  notifications: {
    email: boolean;
    sms: boolean;
    whatsapp: boolean;
    [key: string]: boolean;
  };
}
import { NOTIFICATION_CHANNELS } from '@/utils/notificationChannels';
import { toastMessage } from '@/utils/toast-message';
import React from 'react';

const defaultSettings: NotificationSetting[] = [
    {
        uuid: '1',
        name: 'Allow Notifications',
        description: 'Stay updated with Notifications',
        enabled: true,
        notifications: {
            email: true,
            sms: true,
            whatsapp: false,
        },
    },
];

const NotificationSettings: React.FC = () => {
    // Using useRef to maintain state without triggering re-renders during hydration
    const [settings, setSettings] = React.useState(() => defaultSettings);

    const handleToggleSetting = React.useCallback((settingId: string) => {
        setSettings(prevSettings =>
            prevSettings.map(setting =>
                setting.uuid === settingId
                    ? { ...setting, enabled: !setting.enabled }
                    : setting
            )
        );
    }, []);

    const handleToggleChannel = React.useCallback((settingId: string, channelId: string) => {
        setSettings(prevSettings =>
            prevSettings.map(setting =>
                setting.uuid === settingId
                    ? {
                        ...setting,
                        notifications: {
                            ...setting.notifications,
                            [channelId]: !setting.notifications[channelId],
                        },
                    }
                    : setting
            )
        );
    }, []);

    const handleSettings = React.useCallback(async () => {
        const params = settings.map(setting => ({
            id: setting.uuid,
            enabled: setting.enabled,
            notifications: setting.notifications,
        }));

        try {
            const response = await getLeadNotifications(params);
            toastMessage(response.message || 'Success', 'success')
            console.log('Settings successfully saved:', response);
        } catch (error: any) {
            toastMessage(error.message || 'error', 'error')
            console.error('Error saving settings:', error);
        }
    }, [settings]);

    return (
        <div className="w-full max-w-3xl p-4 space-y-4">
            {settings.map(setting => (
                <div
                    key={setting.uuid}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                    <div className="p-4 flex items-start justify-between">
                        <div className="space-y-1">
                            <h3 className="font-medium text-gray-900">{setting.name}</h3>
                            <p className="text-sm text-gray-500">{setting.description}</p>
                        </div>
                        <Toggle
                            checked={setting.enabled}
                            onChange={() => handleToggleSetting(setting.uuid)}
                            className="ml-4"
                        />
                    </div>

                    {setting.enabled && (
                        <div className="px-4 pb-4 border-t border-gray-100">
                            <div className="pt-4">
                                <div className="text-sm font-medium text-gray-900 mb-4">
                                    Notification channels:
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {NOTIFICATION_CHANNELS.map(channel => (
                                        <div key={channel.id} className="flex items-center space-x-3">
                                            <Toggle
                                                checked={setting.notifications[channel.id]}
                                                onChange={() => handleToggleChannel(setting.uuid, channel.id)}
                                            />
                                            <ChannelIcon iconName={channel.icon} className="h-4 w-4 text-gray-500" />
                                            <span className="text-sm text-gray-600">{channel.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className='mt-5'>
                                <Button
                                    type="button"
                                    onClick={handleSettings}
                                    btnStyle='brandBtn'
                                >
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            ))}

        </div>
    );
};

export default NotificationSettings;